import { apiFetch } from "./apiFetch";
import { getApiErrorMessage, normalizeFetchError } from "./apiError";

// src/api/mpk.ts
const API_URL = process.env.REACT_APP_API_URL;

export type DimensionValue = {
  systemId?: string;
  code: string;
  name: string;
  dimensionCode?: string;
};

export type MPKDimensionDefinition = {
  key: string;
  order: number;
  code: string;
  name: string;
  source: "global" | "local";
  values: DimensionValue[];
};

export type MPKDictionariesResponse = {
  setup: Record<string, any>;
  useMPKDimension: boolean;
  dimensions: MPKDimensionDefinition[];

  /**
   * Tymczasowa kompatybilność ze starą logiką useMpkDimensions.
   * Usuniemy później, gdy hook zostanie całkowicie przepisany na dimensions[].
   */
  dimension1?: {
    code: string;
    name: string;
    values: DimensionValue[];
  };

  dimension2?: {
    code: string;
    name: string;
    values: DimensionValue[];
  };
};

export type MPKRowDTO = {
  systemId: string;
  eDocumentId?: string;
  purchaseLineNo: number;

  amount: number;
  purchaseCost?: string;
  purchaseCostText?: string;
  costType?: string;
  userEmail?: string;

  "@odata.etag"?: string;

  [key: `dimensionCode${number}`]: string | undefined;
  [key: `dimensionValueCode${number}`]: string | undefined;
};

export type SaveMPKDimensionPayload = {
  dimensionCode: string;
  dimensionValueCode: string;
};

export type SaveMPKRowPayload = {
  purchaseLineNo: number;
  amount: number;
  purchaseCost?: string;
  dimensions: SaveMPKDimensionPayload[];
};

export type MPKChangeLogDTO = {
  systemId: string;
  dimensionCode1: string;
  dimensionValueCode1: string;
  dimensionCode2?: string;
  dimensionValueCode2?: string;
  amount: number;
  purchaseCostText: string;
  action: string;
  systemCreatedAt: string;
  systemCreatedBy: string;
  userEMail?: string;
};

// ---------------- API calls ----------------

export async function getMPKDictionaries(companyId: string) {
  const res = await apiFetch(
    `${API_URL}/documents/mpk/dictionaries?companyId=${companyId}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać słowników MPK.")
    );
  }

  return res.json() as Promise<MPKDictionariesResponse>;
}

export async function getDocumentMPK(
  companyId: string,
  documentId: string
) {
  const res = await apiFetch(
    `${API_URL}/documents/${documentId}/mpk?companyId=${companyId}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać danych MPK dokumentu.")
    );
  }

  const data = await res.json();
  return data.value as MPKRowDTO[];
}

export async function saveDocumentMPK(
  companyId: string,
  documentId: string,
  rows: SaveMPKRowPayload[]
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/mpk?companyId=${companyId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(rows),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zapisać danych MPK dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zapisać danych MPK");
  }
}

export async function getMPKChangeLog(
  companyId: string,
  documentId: string
) {
  const res = await apiFetch(
    `${API_URL}/documents/${documentId}/mpk/change-log?companyId=${companyId}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać historii zmian MPK dokumentu.")
    );
  }

  const data = await res.json();
  return data.value as MPKChangeLogDTO[];
}